import React, { useState } from 'react';
import { destinations } from '@/data/destinations';
import { MapPin } from 'lucide-react';

const MAP_IMAGE =
  'https://d64gsuwffb70l.cloudfront.net/69c20284ac08f4efe2afa592_1779392176985_166d9965.png';

interface MapRegion {
  id: string;
  name: string;
  destinations: typeof destinations;
}

const DestinationMap: React.FC = () => {
  const [activeRegion, setActiveRegion] = useState<string | null>(null);

  const regions: MapRegion[] = [
    {
      id: 'northern-albania',
      name: 'Northern Albania',
      destinations: destinations.filter((d) => d.region === 'Northern Albania'),
    },
    {
      id: 'central-albania',
      name: 'Central Albania',
      destinations: destinations.filter((d) => d.region === 'Central Albania'),
    },
    {
      id: 'southern-albania',
      name: 'Southern Albania',
      destinations: destinations.filter(
        (d) => d.region === 'Southern Albania' || d.region === 'Albanian Riviera'
      ),
    },
  ];

  const activeRegionData = regions.find((r) => r.id === activeRegion);

  return (
    <section className="py-24 lg:py-32 bg-[#F5F1E8]">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-12 h-[1px] bg-[#C9A961]" />
            <span className="font-sans text-[10px] tracking-[0.4em] uppercase text-[#C9A961]">
              Explore the Regions
            </span>
            <div className="w-12 h-[1px] bg-[#C9A961]" />
          </div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#1A3B5C] font-light mb-6">
            Discover Albania
          </h2>
          <p className="font-sans text-sm text-[#666] max-w-xl mx-auto leading-relaxed">
            From the sun-kissed Riviera to the dramatic northern Alps — a hand-illustrated
            guide to the regions, heritage cities, castles and natural wonders awaiting you across Albania.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-16 items-start">
          {/* Map Image */}
          <div className="lg:col-span-3">
            <div className="relative bg-[#FAF3E4] border border-[#C9A961]/20 p-4 md:p-6 shadow-sm">
              <img
                src={MAP_IMAGE}
                alt="Illustrated map of Albania showing Northern, Central and Southern regions with key destinations — Shkodër, Theth, Komani Lake, Tirana, Berat, Korçë, Vlorë, Gjirokastër, Sarandë and Butrint"
                className="w-full h-auto block"
                loading="lazy"
              />
            </div>
            <p className="text-center font-sans text-[10px] tracking-[0.3em] uppercase text-[#C9A961] mt-6">
              A Curated Cartography of Albania
            </p>
          </div>

          {/* Region Details Panel */}
          <div className="lg:col-span-2">
            <h3 className="font-serif text-2xl text-[#1A3B5C] font-light mb-6">
              Explore by Region
            </h3>
            <div className="space-y-3 mb-8">
              {regions.map((region) => (
                <button
                  key={region.id}
                  onClick={() =>
                    setActiveRegion(activeRegion === region.id ? null : region.id)
                  }
                  className={`w-full text-left flex items-center justify-between px-5 py-4 border transition-all duration-300 ${
                    activeRegion === region.id
                      ? 'border-[#C9A961] bg-[#C9A961]/10'
                      : 'border-[#1A3B5C]/15 hover:border-[#C9A961]/60 bg-white/40'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span
                      className={`w-3 h-3 rounded-full border ${
                        activeRegion === region.id
                          ? 'bg-[#C9A961] border-[#C9A961]'
                          : 'border-[#1A3B5C]/40'
                      }`}
                    />
                    <span className="font-sans text-[11px] tracking-[0.25em] uppercase text-[#1A3B5C]">
                      {region.name}
                    </span>
                  </div>
                  <span className="font-sans text-[10px] text-[#999]">
                    {region.destinations.length}
                  </span>
                </button>
              ))}
            </div>

            {activeRegionData && activeRegionData.destinations.length > 0 ? (
              <div className="animate-fade-in border-t border-[#C9A961]/20 pt-6">
                <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#C9A961] mb-5">
                  {activeRegionData.destinations.length}{' '}
                  {activeRegionData.destinations.length === 1
                    ? 'Destination'
                    : 'Destinations'}
                </p>
                <div className="space-y-5 max-h-[420px] overflow-y-auto pr-2">
                  {activeRegionData.destinations.map((dest) => (
                    <div
                      key={dest.id}
                      className="flex gap-4 group cursor-pointer"
                      onClick={() => {
                        const el = document.getElementById('destinations');
                        if (el) el.scrollIntoView({ behavior: 'smooth' });
                      }}
                    >
                      <div className="w-24 h-20 flex-shrink-0 overflow-hidden">
                        <img
                          src={dest.image}
                          alt={dest.alt || `${dest.name} in ${dest.region}, Albania`}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-serif text-lg text-[#1A3B5C] group-hover:text-[#C9A961] transition-colors truncate">
                          {dest.name}
                        </h4>
                        <div className="flex items-center gap-1 mb-2">
                          <MapPin className="w-3 h-3 text-[#C9A961]" />
                          <span className="font-sans text-[10px] text-[#999] truncate">
                            {dest.location}
                          </span>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const el = document.getElementById('plan-journey');
                            if (el) el.scrollIntoView({ behavior: 'smooth' });
                          }}
                          className="font-sans text-[9px] tracking-[0.2em] uppercase px-3 py-1.5 bg-[#1A3B5C]/10 text-[#1A3B5C] hover:bg-[#1A3B5C]/20 transition-colors"
                        >
                          Book Now
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="border-t border-[#C9A961]/20 pt-6">
                <p className="font-sans text-xs text-[#777] leading-relaxed">
                  Select a region above to reveal the curated destinations our concierge team
                  recommends across that corner of Albania.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default DestinationMap;
